import { useCallback, useMemo } from 'react';
import { APP_NAME_PATTERN } from '../../utils/customApp';
import { useModules } from './useModules';
import type { ModulesApiResponse } from './useModules';

/** `appName` → the remote that serves it, or `undefined` for "its own id". */
export type RemoteAppLookup = (appName: string) => string | undefined;

/** `appName` → its registered display name, or `undefined` when unnamed. */
export type AppNameLookup = (appName: string) => string | undefined;

export interface AppRegistry {
  remoteAppFor: RemoteAppLookup;
  /**
   * The human name a client gave the app when registering it — the same string
   * the left nav shows.
   *
   * Custom-app routes are keyed by the registry id, and a client is free to
   * register one under an opaque id (a UUID, on the tenants that generate them
   * rather than hand-writing slugs). Passing that id on as the app's title
   * surfaces it verbatim to the user: the Genie header rendered
   * "Locus 01f1a4e407c51a20a4e55c694f2ee1a5" because the id was the only thing
   * the host ever sent.
   *
   * `undefined` when the registry has no name for the id, so callers keep
   * whatever they show today rather than rendering a blank title.
   */
  nameFor: AppNameLookup;
  /**
   * `false` only while the first `/modules` fetch is in flight. Callers hold the
   * MFE mount until then: resolving early with the fallback would register the
   * remote under a wrong manifest URL and fail loudly before the alias arrives.
   * A fetch error or a missing clientId counts as ready — the lookup is then
   * empty and every app falls back to its own id, which is today's behaviour.
   */
  isReady: boolean;
}

/** The `apps` map carried by the `/modules` envelope, if the payload has one. */
function appsOf(
  response: ModulesApiResponse | undefined
): Record<string, { name?: unknown; remoteApp?: unknown } | null> {
  const apps = (response?.data as { apps?: unknown } | undefined)?.apps;
  return typeof apps === 'object' && apps !== null
    ? (apps as Record<string, { name?: unknown; remoteApp?: unknown } | null>)
    : {};
}

/**
 * Project the `/modules` `apps` map into `id → remoteApp`. Only entries that
 * actually redirect are kept: a `remoteApp` equal to the app's own id, a
 * non-string, or a value outside `APP_NAME_PATTERN` (it becomes a URL path
 * segment) is treated as absent. Pure, so it is unit-testable off the network.
 */
export function buildRemoteAppMap(
  response: ModulesApiResponse | undefined
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const [id, record] of Object.entries(appsOf(response))) {
    const remoteApp = record?.remoteApp;
    if (
      typeof remoteApp === 'string' &&
      remoteApp !== id &&
      APP_NAME_PATTERN.test(remoteApp)
    ) {
      map.set(id, remoteApp);
    }
  }
  return map;
}

/**
 * Project the `/modules` `apps` map into `id → name`.
 *
 * Only entries that carry a usable name are kept: a missing, non-string or
 * blank `name` is treated as absent so `nameFor` returns `undefined` and the
 * caller falls back to the id, rather than rendering an empty title.
 * Deliberately does NOT constrain the name to `APP_NAME_PATTERN` — that
 * grammar governs ids, which become URL path segments; a display name is free
 * text ("Order & Fulfilment Agent") and validating it against the id grammar
 * would reject every realistic name.
 */
export function buildAppNameMap(
  response: ModulesApiResponse | undefined
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const [id, record] of Object.entries(appsOf(response))) {
    const name = record?.name;
    if (typeof name === 'string' && name.trim()) {
      map.set(id, name.trim());
    }
  }
  return map;
}

/**
 * The client's app registry, reduced to the two lookups the custom-app hosts
 * need (`CustomAppPage`, `EntityGridView`, `EntityDetails`,
 * `CustomAppActionModal`): which bundle serves an app, and what to call it.
 *
 * Read from the `/modules` payload the dashboard already fetches (DP-5201). It
 * used to be a search of the `apps` entity, which needs an entity read scope
 * that client users do not have — their first page load answered 403, and
 * both lookups came back empty for exactly the users who see custom apps. The
 * backend serves the map from its own registry cache, so it covers every
 * registered app, not just the ones reachable from the navigation tree:
 * custom apps also mount from workflow actions and from entity tabs.
 */
export function useAppRegistry(clientId: string): AppRegistry {
  const { data, isLoading } = useModules(clientId);

  const remoteApps = useMemo(() => buildRemoteAppMap(data), [data]);
  const remoteAppFor = useCallback<RemoteAppLookup>(
    appName => remoteApps.get(appName),
    [remoteApps]
  );

  // Same payload, same memo dependency as the alias map — reading the name is
  // a second projection of one response, not a second fetch.
  const appNames = useMemo(() => buildAppNameMap(data), [data]);
  const nameFor = useCallback<AppNameLookup>(
    appName => appNames.get(appName),
    [appNames]
  );

  return { remoteAppFor, nameFor, isReady: !isLoading };
}
